/**
 * Listings Transformer
 *
 * Transforms backend composed DTOs to frontend Listing types.
 * Backend now returns nested snake_case structures with LocalizedText.
 * No more camelCase mapping - direct pass-through of backend structure.
 */

import type { Listing, ListingsSearchParams } from "@/shared/types/listings";
import type { LocalizedText } from "@/shared/types/primitives";
import type { QueryParamConfig } from "@/shared/lib/utils/queryString";
import {
  buildQueryString,
  createBooleanParamConfigs,
  createNumberParamConfigs,
  createStringParamConfigs,
} from "@/shared/lib/utils/queryString";

// ============================================
// Backend API Response Types (Composed DTOs)
// ============================================

/**
 * Backend API listing response (matches ListingResponse DTO).
 * Already in composed snake_case structure with LocalizedText.
 */
export interface ApiListingResponse {
  // Identifiers
  id: string;
  org_id: string;
  source: string;
  source_url?: string;

  // Status
  publication_status: string;
  published_at?: string;
  availability_status: string;
  available_from?: string;

  // Composed blocks (LocalizedText already)
  title: LocalizedText;
  description: LocalizedText;
  location: {
    prefecture: LocalizedText;
    city: LocalizedText;
    ward?: LocalizedText;
    address_display: LocalizedText;
    stations: Array<{
      name: LocalizedText;
      walk_minutes: number;
      distance_m?: number;
      line_name?: LocalizedText;
      line_code?: string;
      line_color?: string;
    }>;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  financials: {
    rent: {
      amount: number;
      currency: "JPY";
    };
    management_fee?: {
      amount: number;
      currency: "JPY";
    };
    deposit?: {
      amount: number;
      currency: "JPY";
    };
    key_money?: {
      amount: number;
      currency: "JPY";
    };
    guarantor_required: boolean;
    renewal_fee?: {
      amount: number;
      currency: "JPY";
    };
    insurance_fee?: {
      amount: number;
      currency: "JPY";
    };
  };
  property: {
    property_type: string;
    structure?: LocalizedText;
    room_layout: string;
    area_sqm: number;
    balcony_area_sqm?: number;
    floor?: number;
    total_floors?: number;
    year_built?: number;
  };
  amenities: {
    bathroom: {
      separate: boolean;
      dryer: boolean;
      washlet: boolean;
      reheating: boolean;
      independent_vanity: boolean;
    };
    kitchen: {
      system_kitchen: boolean;
      dishwasher: boolean;
      gas_stove: boolean;
      ih_cooktop: boolean;
      counter_kitchen: boolean;
    };
    security: {
      auto_lock: boolean;
      video_intercom: boolean;
      delivery_box: boolean;
      security_camera: boolean;
    };
    contract: {
      pets_allowed: boolean;
      foreigner_friendly: boolean;
      instruments_ok: boolean;
      soho_allowed: boolean;
    };
    comfort: {
      air_conditioning: boolean;
      flooring_wood: boolean;
      south_facing: boolean;
      corner_unit: boolean;
    };
    connectivity: {
      internet_included: boolean;
    };
    parking: {
      parking: boolean;
      bicycle_parking: boolean;
      motorcycle_parking: boolean;
    };
    laundry: {
      washer_hookup: boolean;
      washer_dryer_in_unit: boolean;
      indoor_washer_space: boolean;
    };
    building: {
      elevator: boolean;
      balcony: boolean;
    };
  };
  media: {
    images_by_section: Record<
      string,
      Array<{
        url: string;
        thumbnail_url: string;
        caption?: LocalizedText;
        is_featured: boolean;
      }>
    >;
  };

  timestamps: {
    created_at: string;
    updated_at: string;
  };

  metadata: Record<string, unknown>;
}

// ============================================
// Transformation Functions
// ============================================

/**
 * Transform API listing response to frontend Listing type.
 * Backend already returns composed structure - mostly pass-through.
 *
 * @param data - Backend API response
 * @returns Frontend Listing type
 */
export function transformListing(data: ApiListingResponse): Listing {
  // Backend structure already matches frontend - direct assignment
  return {
    id: data.id,
    org_id: data.org_id,
    source: data.source,
    source_url: data.source_url,
    publication_status: data.publication_status,
    published_at: data.published_at,
    availability_status: data.availability_status,
    available_from: data.available_from,
    title: data.title,
    description: data.description,
    location: data.location,
    financials: data.financials,
    property: data.property,
    amenities: data.amenities,
    media: data.media,
    timestamps: data.timestamps,
    metadata: data.metadata,
  };
}

/**
 * Transform array of API listings to frontend Listing array.
 *
 * @param listings - Array of backend API responses
 * @returns Array of frontend Listing types
 */
export function transformListings(listings: ApiListingResponse[]): Listing[] {
  return listings.map(transformListing);
}

// ============================================
// Query Parameter Configuration (snake_case)
// ============================================

/** String parameters with "any" skip value */
const STRING_PARAMS_WITH_ANY = [
  "key_money",
  "deposit",
  "guarantor_required",
  "building_age",
  "floor",
  "walk_to_station",
] as const;

/** Plain string parameters */
const STRING_PARAMS = [
  "locale", // REQUIRED - i18n query parameter (derived from LocalizedText keys)
  "prefecture",
  "city",
  "ward",
  "property_type",
  "room_layout",
  "cursor",
] as const;

/** Number parameters */
const NUMBER_PARAMS = [
  "rent_min",
  "rent_max",
  "size_min",
  "size_max",
  "limit",
] as const;

/** Boolean amenity filter parameters (snake_case) */
const BOOLEAN_AMENITY_PARAMS = [
  "air_conditioning",
  "washer_hookup",
  "washer_dryer_in_unit",
  "bath_toilet_separate",
  "balcony",
  "flooring_wood",
  "internet_included",
  "gas_stove",
  "ih_cooktop",
  "elevator",
  "auto_lock",
  "delivery_box",
  "parking",
  "bicycle_parking",
] as const;

/** Boolean contract filter parameters (snake_case) */
const BOOLEAN_CONTRACT_PARAMS = [
  "foreigner_friendly",
  "pets_allowed",
  "instruments_ok",
  "no_guarantor_needed",
  "move_in_within_30_days",
] as const;

/** Special boolean parameter */
const INCLUDE_MANAGEMENT_FEE_PARAM = "include_management_fee";

/**
 * Complete query parameter configuration for listings search (snake_case).
 */
export const LISTINGS_QUERY_CONFIG: QueryParamConfig[] = [
  ...createStringParamConfigs(STRING_PARAMS),
  ...createStringParamConfigs(STRING_PARAMS_WITH_ANY, "any"),
  ...createNumberParamConfigs(NUMBER_PARAMS),
  ...createBooleanParamConfigs(BOOLEAN_AMENITY_PARAMS),
  ...createBooleanParamConfigs(BOOLEAN_CONTRACT_PARAMS),
  { key: INCLUDE_MANAGEMENT_FEE_PARAM, type: "boolean", onlyIfTrue: false },
];

/**
 * All boolean filter keys for URL parsing (snake_case).
 */
export const BOOLEAN_FILTER_KEYS = [
  ...BOOLEAN_AMENITY_PARAMS,
  ...BOOLEAN_CONTRACT_PARAMS,
] as const;

/**
 * Build query string for listings search API (snake_case params).
 *
 * @param params - Search parameters (snake_case)
 * @returns URL-encoded query string
 */
export function buildListingsQueryString(params: ListingsSearchParams): string {
  return buildQueryString(
    params as unknown as Record<string, unknown>,
    LISTINGS_QUERY_CONFIG,
  );
}
